import React, { useState, useRef } from "react";
import { Box, Typography, Stack, Button, IconButton, Divider } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import CallSplitIcon from "@mui/icons-material/CallSplit";
import CurrencyTextField from "../../shared/UIParts/InputFields/CurrencyTextField";
import { MimshakData } from "../../interfaces/DTO/mimshakData";
import { formatCurrency } from "../../utils/namesFormater";

interface Props {
  mimshak: MimshakData;
  onSplitProduct: (sourceMimshak: MimshakData, splitAmounts: number[]) => void;
}

const SplitProductSection: React.FC<Props> = ({ mimshak, onSplitProduct }) => {
  const [splitExpanded, setSplitExpanded] = useState(false);
  const [splitAmounts, setSplitAmounts] = useState<(number | null)[]>([0]);
  const firstSplitRef = useRef<HTMLInputElement>(null);
  const lastSplitRef = useRef<HTMLInputElement>(null);

  const totalFundsSum = mimshak.funds_sum ?? 0;

  const handleAddSplitRow = () => {
    setSplitAmounts((prev) => [...prev, 0]);
    setTimeout(() => lastSplitRef.current?.focus(), 50);
  };

  const handleSplitAmountChange = (index: number, value: number | null) => {
    setSplitAmounts((prev) => {
      const next = prev.map((v, i) => (i === index ? value : v));
      if (index === 0 && next.length === 2) {
        const remaining = value !== null ? totalFundsSum - value : null;
        next[1] = remaining !== null && remaining > 0 ? remaining : null;
      }
      return next;
    });
  };

  const handleRemoveSplitRow = (index: number) => {
    setSplitAmounts((prev) => prev.filter((_, i) => i !== index));
  };

  const splitTotal = splitAmounts.reduce((s, v) => s + (v ?? 0), 0);
  const splitOverflow = splitTotal > totalFundsSum + 0.01;
  const splitUnderflow = splitTotal < totalFundsSum - 0.01;

  const splitValid =
    splitAmounts.length >= 1 &&
    splitAmounts.every((v) => v !== null && v > 0) &&
    !splitOverflow &&
    splitTotal < totalFundsSum;

  const handleConfirmSplit = () => {
    if (!splitValid) return;
    const amounts = splitAmounts as number[];
    const remaining = totalFundsSum - amounts.reduce((s, v) => s + v, 0);
    const finalAmounts = remaining > 0.01 ? [...amounts, remaining] : amounts;
    onSplitProduct(mimshak, finalAmounts);
    setSplitAmounts([]);
    setSplitExpanded(false);
  };

  return (
    <Box mt={4}>
      <Divider sx={{ mb: 2 }} />
      <Button
        startIcon={<CallSplitIcon sx={{ ml: 1 }} />}
        variant={splitExpanded ? "contained" : "outlined"}
        color="warning"
        fullWidth
        onClick={() => {
          const opening = !splitExpanded;
          setSplitExpanded(opening);
          if (opening) {
            setSplitAmounts([0]);
            setTimeout(() => firstSplitRef.current?.focus(), 50);
          } else {
            setSplitAmounts([]);
          }
        }}
      >
        פצל קופה
      </Button>

      {splitExpanded && (
        <Box mt={2}>
          <Typography variant="subtitle1" gutterBottom>
            סכום קופה: {formatCurrency(totalFundsSum)}
          </Typography>
          {/* <Typography variant="caption" color="text.secondary" display="block" mb={1}>
            הסכום הראשון שתזין יישאר במוצר המקורי.
          </Typography> */}
          <Stack spacing={1.5}>
            {splitAmounts.map((val, idx) => (
              <Stack key={idx} direction="row" spacing={1} alignItems="center">
                <CurrencyTextField
                  label={idx === 0 ? "סכום שישאר במוצר המקורי" : `פיצול ${idx}`}
                  value={val}
                  onChange={(v) => handleSplitAmountChange(idx, v)}
                
                  maxWidth="100%"
                  sx={{ flex: 1 }}
                  error={val !== null && val <= 0}
                  inputRef={idx === 0 ? firstSplitRef : idx === splitAmounts.length - 1 ? lastSplitRef : undefined}
                />
                <IconButton size="small" onClick={() => handleRemoveSplitRow(idx)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Stack>
            ))}
          </Stack>
   <Button
            startIcon={<AddIcon />}
          
            onClick={handleAddSplitRow}
            sx={{ mt: 1 }}
          >
            הוסף פיצול
          </Button>
          {splitAmounts.length > 0 && (
            <Box mt={1}>
              {splitOverflow && (
                <Typography variant="caption" color="error">
                  סך הפיצולים ({splitTotal.toLocaleString("he-IL")} ₪) עולה על סכום הקופה
                </Typography>
              )}
              {splitUnderflow && splitAmounts.every((v) => v !== null) && (
                <Typography variant="subtitle2" color="info.main">
                  {(totalFundsSum - splitTotal).toLocaleString("he-IL")} ₪ ייווצרו כמוצר חדש נוסף
                </Typography>
              )}
              {!splitOverflow && !splitUnderflow && splitAmounts.every((v) => v !== null) && (
                <Typography variant="subtitle2" color="success.main">
                  סך הפיצולים תואם את סכום הקופה
                </Typography>
              )}
            </Box>
          )}

       
          <Button
            variant="contained"
            color="success"
            fullWidth
            disabled={!splitValid}
            onClick={handleConfirmSplit}
            sx={{ mt: 2 }}
          >
            אשר פיצול
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default SplitProductSection;
